import {parseISO} from 'date-fns';
import {Column, DataStatus, Flag, IconButton, Label, Table, TableRow} from 'platform/components';
import {Box, Hide, HStack, Link, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  accountingApi,
  useRenderCorrectiveTaxDocumentMutation,
  useRenderIncomeCashReceiptDocumentMutation,
  useRenderTaxDocumentForPaymentDocumentMutation,
} from '@dms/api/finance';
import {
  Payment,
  useGetCheckoutPaymentTransactionsQuery,
  usePostCheckoutPaymentTransactionCancelMutation,
  usePostCheckoutPaymentTransactionIssueTaxDocumentMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {
  buildArray,
  convertStringToCamelCase,
  EMPTY_PLACEHOLDER,
  openFile,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

interface PaymentsListProps extends RequiredTestIdProps {
  checkoutId: string;
  payment: Payment;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
}

export function PaymentsList(props: PaymentsListProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatCurrency = useFormatCurrency();

  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    isFetching: isFetchingTransactions,
    isError: isErrorTransactions,
  } = useGetCheckoutPaymentTransactionsQuery({
    checkoutId: props.checkoutId,
    paymentId: props.payment.checkoutPaymentId,
  });

  const [cancelTransaction, {isLoading: isWithdrawalPending}] =
    usePostCheckoutPaymentTransactionCancelMutation();

  const [postCheckoutPaymentTransactionIssueTaxDocument, {isLoading: isCreatingDocument}] =
    usePostCheckoutPaymentTransactionIssueTaxDocumentMutation();

  const [renderTaxDocumentForPayment, {isLoading: isRenderingTaxDocumentForPayment}] =
    useRenderTaxDocumentForPaymentDocumentMutation();

  const [renderIncomeCashReceipt, {isLoading: isRenderingIncomeCashReceipt}] =
    useRenderIncomeCashReceiptDocumentMutation();

  const [renderCorrectiveTaxDocument, {isLoading: isRenderingCorrectiveTaxDocument}] =
    useRenderCorrectiveTaxDocumentMutation();

  const openTaxDocumentForPayment = (documentId: string) =>
    renderTaxDocumentForPayment({renderDocumentRequestBody: {documentId}})
      .unwrap()
      .then((data) => data.pdfUrl)
      .then(openFile)
      .catch(handleApiError);

  const openCashReceipt = (cashRegisterDocumentId: string) => {
    renderIncomeCashReceipt({
      renderDocumentRequestBody: {cashRegisterDocumentId},
    })
      .unwrap()
      .then((data) => data.pdfUrl)
      .then(openFile)
      .then(() => {
        accountingApi.util.invalidateTags(['invoicePayment', 'invoiceProformaPaymentList']);
      })
      .catch(handleApiError);
  };

  const openCorrectiveTaxDocument = (documentId: string) =>
    renderCorrectiveTaxDocument({renderDocumentRequestBody: {documentId}})
      .unwrap()
      .then((data) => data.pdfUrl)
      .then(openFile)
      .catch(handleApiError);

  const handleWithdrawPayment = (paymentId: string, transactionId: string) => {
    cancelTransaction({
      checkoutId: props.checkoutId,
      paymentId,
      transactionId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const createTaxDocumentForPayment = (paymentId: string, transactionId: string) => {
    postCheckoutPaymentTransactionIssueTaxDocument({
      checkoutId: props.checkoutId,
      paymentId,
      transactionId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const columns = buildArray<Column>()
    .add({
      width: 20,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('general.labels.type')}</Label>
        </Box>
      ),
    })
    .add({
      width: 35,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.bank.labels.paymentType')}</Label>
        </Box>
      ),
    })
    .add({
      width: 35,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.checkout.labels.amount')}</Label>
        </Box>
      ),
    })
    .add({
      width: 30,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.invoice.labels.dateOfPayment')}</Label>
        </Box>
      ),
    })
    .add({
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.checkout.labels.file')}</Label>
        </Box>
      ),
    })
    .add({
      width: 18,
    });

  const isRendering =
    isRenderingTaxDocumentForPayment ||
    isRenderingIncomeCashReceipt ||
    isRenderingCorrectiveTaxDocument;

  return (
    <DataStatus
      isEmpty={isNilOrEmpty(transactions)}
      isLoading={isLoadingTransactions}
      isError={isErrorTransactions}
      spacing={5}
    >
      <Table
        tableLayout="fixed"
        columns={columns}
        data-testid={suffixTestId('payment-table', props)}
      >
        {transactions?.map((item, index) => (
          <TableRow key={item.transactionId} data-testid={suffixTestId('table-row', props)}>
            <Box padding={2}>
              <Flag
                label={i18n.t(`entity.checkout.labels.${item.source}`)}
                colorScheme={item.source === 'external' ? 'yellow' : 'green'}
                isSubtle
                data-testid={suffixTestId('transaction-source', props)}
              />
            </Box>
            <Box padding={2}>
              <Text size="small">
                {i18n.t(
                  `entity.invoice.paymentMethod.${convertStringToCamelCase(item.paymentType)}`
                )}
              </Text>
            </Box>
            <Box padding={2}>
              <Text size="small">
                {formatCurrency(item.amount.amount, item.amount.currency, 2)}
              </Text>
            </Box>
            <Box padding={2}>
              <Text size="small">{formatDateTime('dateShort', parseISO(item.paymentDate))}</Text>
            </Box>
            <Box padding={2}>
              <Show when={item.taxDocumentForPaymentId}>
                <Link
                  isDisabled={isRendering}
                  size="small"
                  title={`${i18n.t('entity.accounting.labels.taxDocumentForPayment')} ${
                    item.taxDocumentForPaymentNumber
                  }`}
                  onClick={() => openTaxDocumentForPayment(item.taxDocumentForPaymentId!)}
                  data-testid={suffixTestId('transaction-taxDocumentForPayment', props)}
                />
              </Show>
              <Show when={item.cashRegisterDocumentId}>
                <Link
                  isDisabled={isRendering}
                  size="small"
                  title={`${i18n.t('entity.checkout.labels.cashReceipt')} ${
                    item.cashRegisterDocumentNumber
                  }`}
                  onClick={() => openCashReceipt(item.cashRegisterDocumentId!)}
                  data-testid={suffixTestId('transaction-cashReceipt', props)}
                />
              </Show>
              <Show when={item.correctiveTaxDocumentForTaxDocumentForPaymentId}>
                <Link
                  isDisabled={isRendering}
                  size="small"
                  title={`${i18n.t('entity.checkout.labels.correctiveTaxDocument')} ${
                    item.correctiveTaxDocumentForTaxDocumentForPaymentNumber
                  }`}
                  onClick={() =>
                    openCorrectiveTaxDocument(item.correctiveTaxDocumentForTaxDocumentForPaymentId!)
                  }
                  data-testid={suffixTestId('transaction-correctiveTaxDocument', props)}
                />
              </Show>
              <Hide
                when={
                  item.taxDocumentForPaymentId ||
                  item.cashRegisterDocumentId ||
                  item.correctiveTaxDocumentForTaxDocumentForPaymentId
                }
              >
                {EMPTY_PLACEHOLDER}
              </Hide>
            </Box>
            <HStack justify="flex-end">
              <Show when={item.allowedActions?.includes('TAX_DOCUMENT_CREATION')}>
                <IconButton
                  icon="editor/insert_drive_file"
                  onClick={() => createTaxDocumentForPayment(item.paymentId, item.transactionId)}
                  data-testid={suffixTestId(`payment-${index}-createTaxDocumentForPayment`, props)}
                  isDisabled={
                    isCreatingDocument ||
                    isFetchingTransactions ||
                    props.isEditingDisabled ||
                    props.isCompletingDisabled
                  }
                />
              </Show>
              <Show when={item.allowedActions?.includes('TRANSACTION_CANCELLATION')}>
                <IconButton
                  severity="danger"
                  icon="action/delete"
                  onClick={() => handleWithdrawPayment(item.paymentId, item.transactionId)}
                  isDisabled={
                    isWithdrawalPending ||
                    isFetchingTransactions ||
                    props.isEditingDisabled ||
                    props.isCompletingDisabled
                  }
                  data-testid={suffixTestId(`payment-${index}-transaction-delete`, props)}
                />
              </Show>
            </HStack>
          </TableRow>
        ))}
      </Table>
    </DataStatus>
  );
}
